import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listReports, deleteReport } from "../lib/api";
import { toast } from "sonner";
import {
  Bookmark, ExternalLink, Trash2, Share2, ChevronRight, FileText, Clock, CheckSquare,
} from "lucide-react";

const SEV = {
  critical: "bg-red-500/10 text-red-400 border-red-500/20",
  warning: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  info: "bg-blue-500/10 text-blue-400 border-blue-500/20",
};

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => listReports().then((d) => { setReports(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await deleteReport(id);
      setReports((r) => r.filter((x) => x.id !== id));
      toast.success("Report deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const share = (shareId) => {
    const url = `${window.location.origin}/report/${shareId}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied to clipboard");
  };

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> reports <ChevronRight className="h-3 w-3" /> saved runs
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-4xl">
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight">Session Reports</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Saved diagnostic runs you bookmarked from the wizard. Share a link with a colleague or export a printable report.
        </p>

        {loading ? (
          <div className="mt-8 text-zinc-500 font-mono text-sm">Loading reports…</div>
        ) : reports.length === 0 ? (
          <div className="mt-8 rounded-lg border border-dashed border-zinc-800 bg-zinc-900 p-10 text-center">
            <Bookmark className="h-8 w-8 text-zinc-600 mx-auto" />
            <p className="text-zinc-400 mt-3">No saved reports yet.</p>
            <button
              onClick={() => navigate("/")}
              data-testid="reports-empty-cta"
              className="mt-4 text-blue-400 hover:underline text-sm"
            >
              Open a diagnostic and click "Save Report" to bookmark it.
            </button>
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="reports-list">
            {reports.map((r) => (
              <div key={r.id} data-testid={`report-${r.id}`} className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileText className="h-4 w-4 text-blue-400 shrink-0" />
                      <h3 className="font-heading font-bold text-white text-lg">{r.symptom_title}</h3>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${SEV[r.symptom_severity] || SEV.info}`}>
                        {r.symptom_severity}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-[11px] text-zinc-500">
                      <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{new Date(r.created_at).toLocaleString()}</span>
                      <span className="flex items-center gap-1.5"><CheckSquare className="h-3.5 w-3.5" />{r.completed_steps?.length || 0} steps marked done</span>
                    </div>
                    {r.notes && <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{r.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => share(r.share_id)} data-testid={`report-share-${r.id}`} title="Copy share link"
                      className="h-9 w-9 flex items-center justify-center rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors">
                      <Share2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => navigate(`/report/${r.share_id}`)} data-testid={`report-open-${r.id}`} title="Open report"
                      className="h-9 w-9 flex items-center justify-center rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors">
                      <ExternalLink className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(r.id)} data-testid={`report-delete-${r.id}`} title="Delete"
                      className="h-9 w-9 flex items-center justify-center rounded-md bg-zinc-800 hover:bg-red-600/80 text-zinc-300 hover:text-white transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
